import React, { useState, useRef } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { AlertTriangle, Banknote, Calendar, Check, ChevronRight, Clapperboard, ClipboardList, Folder, Pencil, Percent, Plus, Settings2, Tag, Trash2, Users, Wallet, X } from 'lucide-react';
import { toast } from 'sonner';
import { apiPatch, apiPost, apiDelete, apiGet } from '../../../api/client';
import type {
  DiscountType, EventBudgetSummary, Event, EventExpense, EventExpenseCategory,
  EventPayment, EventService, EventStatus,
} from '../../../api/types';
import { Button } from '../../../components/ui/Button';
import { Input, Select } from '../../../components/ui/Input';
import { Modal } from '../../../components/ui/Modal';
import { ProgressBar } from '../../../components/ui/ProgressBar';
import { formatCurrency, formatDate, formatPaymentMethod, clientFullName } from '../../../utils/format';
import { EXPENSE_CATEGORIES, EXPENSE_CATEGORY_COLORS, TAX_MODE_OPTIONS, effectivePrice, pct, EMPTY_EXPENSE_FORM } from '../../../utils/expense';
import { ExpenseRow } from './shared/ExpenseRow';
import { MarginRow } from './shared/MarginRow';
import { ExpenseModal, getEmptyExpenseForm, type ExpenseFormState } from './shared/ExpenseModal';
import { TaxModal, type TaxFormState } from './shared/TaxModal';

// ─── Constants ────────────────────────────────────────────────────────────────

const STATUS_ORDER: EventStatus[] = ['PREVENTIVO','CONTRATTO','CONFERMATO','IN_LAVORAZIONE','CONSEGNATO','ARCHIVIATO'];

const PAYMENT_METHODS = [
  { value: 'CONTANTI', label: 'Contanti' },
  { value: 'BONIFICO', label: 'Bonifico' },
  { value: 'CARTA',    label: 'Carta' },
  { value: 'PAYPAL',   label: 'PayPal' },
  { value: 'ALTRO',    label: 'Altro' },
];

const DISCOUNT_TYPES = [
  { value: 'PERCENTAGE', label: '% Percentuale' },
  { value: 'FIXED',      label: '€ Importo fisso' },
];

// ─── Component ────────────────────────────────────────────────────────────────

export default function OverviewTab({ event, onSwitchTab }: { event: Event; onSwitchTab?: (tab: string) => void }) {
  const qc = useQueryClient();

  // Payment modals
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
  const [paymentForm, setPaymentForm] = useState({ amount: '', paidAt: '', method: 'BONIFICO', notes: '' });

  const { data: payments = [] } = useQuery({
    queryKey: ['event-payments', event.id],
    queryFn: () => apiGet(`/events/${event.id}/payments`),
    enabled: !!event.id,
  });


  // Inline discount editing
  const [editingDiscountId, setEditingDiscountId] = useState<string | null>(null);
  const [discountForm, setDiscountForm] = useState({ discountType: 'PERCENTAGE', discountValue: '' });

  // Expense modal
  const [showExpenseModal, setShowExpenseModal] = useState(false);
  const [editingExpense,   setEditingExpense]   = useState<EventExpense | null>(null);
  const [expenseForm, setExpenseForm] = useState(EMPTY_EXPENSE_FORM);

  // Tax modal
  const [showTaxModal, setShowTaxModal] = useState(false);
  const [taxForm, setTaxForm] = useState({ taxMode: '', taxRate: '', taxCoefficient: '' });

  // Inline totalAmount editing
  const [editingTotalAmount, setEditingTotalAmount] = useState(false);
  const [totalAmountInput, setTotalAmountInput] = useState('');

  // ── Queries ──────────────────────────────────────────────────────────────

  const { data: budget } = useQuery<EventBudgetSummary>({
    queryKey: ['event-budget', event.id],
    queryFn: () => apiGet<EventBudgetSummary>(`/events/${event.id}/budget-summary`),
  });

  // ── Invalidation ────────────────────────────────────────────────────────

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['event', event.id] });
    qc.invalidateQueries({ queryKey: ['event-budget', event.id] });
  };

  // ── Mutations ────────────────────────────────────────────────────────────

  const statusMutatingRef = useRef(false);

  const statusMutation = useMutation({
    mutationFn: (status: EventStatus) => apiPatch(`/events/${event.id}/status`, { status }),
    onSuccess: () => { invalidate(); toast.success('Stato aggiornato'); },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => { statusMutatingRef.current = false; },
  });

  const addPaymentMutation = useMutation({
    mutationFn: () => apiPost(`/events/${event.id}/payments`, {
      amount: Number(paymentForm.amount),
      paidAt: new Date(paymentForm.paidAt).toISOString(),
      method: paymentForm.method,
      notes: paymentForm.notes || undefined,
    }),
    onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ['event-payments', event.id] }); setShowPaymentModal(false); toast.success('Acconto registrato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const editPaymentMutation = useMutation({
    mutationFn: () => apiPatch(`/events/${event.id}/payments/${editingPaymentId}`, {
      amount: Number(paymentForm.amount),
      paidAt: new Date(paymentForm.paidAt).toISOString(),
      method: paymentForm.method,
      notes: paymentForm.notes || null,
    }),
    onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ['event-payments', event.id] }); setShowPaymentModal(false); setEditingPaymentId(null); toast.success('Acconto aggiornato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const deletePaymentMutation = useMutation({
    mutationFn: (paymentId: string) => apiDelete(`/events/${event.id}/payments/${paymentId}`),
    onSuccess: () => { invalidate(); qc.invalidateQueries({ queryKey: ['event-payments', event.id] }); toast.success('Acconto rimosso'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const removeServiceMutation = useMutation({
    mutationFn: (serviceId: string) => apiDelete(`/events/${event.id}/services/${serviceId}`),
    onSuccess: () => { invalidate(); toast.success('Servizio rimosso'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const patchDiscountMutation = useMutation({
    mutationFn: ({ serviceId, data }: { serviceId: string; data: object }) =>
      apiPatch(`/events/${event.id}/services/${serviceId}`, data),
    onSuccess: () => { invalidate(); setEditingDiscountId(null); toast.success('Sconto aggiornato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const saveExpenseMutation = useMutation({
    mutationFn: (data: object) => editingExpense
      ? apiPatch(`/events/${event.id}/expenses/${editingExpense.id}`, data)
      : apiPost(`/events/${event.id}/expenses`, data),
    onSuccess: () => {
      invalidate();
      setShowExpenseModal(false);
      toast.success(editingExpense ? 'Spesa aggiornata' : 'Spesa aggiunta');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteExpenseMutation = useMutation({
    mutationFn: (id: string) => apiDelete(`/events/${event.id}/expenses/${id}`),
    onSuccess: () => { invalidate(); toast.success('Spesa rimossa'); },
    onError: (e: Error) => toast.error(e.message),
  });

  const togglePaidMutation = useMutation({
    mutationFn: ({ expenseId, isPaid }: { expenseId: string; isPaid: boolean }) =>
      apiPatch(`/events/${event.id}/expenses/${expenseId}`, { isPaid }),
    onSuccess: () => invalidate(),
    onError: (e: Error) => toast.error(e.message),
  });

  const patchTaxMutation = useMutation({
    mutationFn: (data: object) => apiPatch(`/events/${event.id}/tax`, data),
    onSuccess: () => {
      invalidate();
      setShowTaxModal(false);
      toast.success('Regime fiscale aggiornato');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const patchTotalAmountMutation = useMutation({
    mutationFn: (totalAmount: number) => apiPatch(`/events/${event.id}`, { totalAmount }),
    onSuccess: () => { invalidate(); setEditingTotalAmount(false); toast.success('Totale aggiornato'); },
    onError: (e: Error) => toast.error(e.message),
  });

  // ── Discount helpers ─────────────────────────────────────────────────────

  const openDiscount = (es: EventService) => {
    setDiscountForm({
      discountType:  es.discountType ?? 'PERCENTAGE',
      discountValue: es.discount != null ? String(es.discount) : '',
    });
    setEditingDiscountId(es.id);
  };

  const saveDiscount = (serviceId: string) => {
    const val = Number(discountForm.discountValue);
    if (!discountForm.discountValue || isNaN(val) || val < 0) { toast.error('Inserisci un valore sconto valido'); return; }
    patchDiscountMutation.mutate({ serviceId, data: { discountType: discountForm.discountType as DiscountType, discount: val } });
  };

  const removeDiscount = (serviceId: string) =>
    patchDiscountMutation.mutate({ serviceId, data: { discountType: null, discount: null } });

  // ── Expense helpers ──────────────────────────────────────────────────────

  const openAddExpense = () => {
    setEditingExpense(null);
    setExpenseForm(EMPTY_EXPENSE_FORM);
    setShowExpenseModal(true);
  };

  const openEditExpense = (exp: EventExpense) => {
    setEditingExpense(exp);
    setExpenseForm({
      category:        exp.category,
      description:     exp.description,
      amountEstimated: String(exp.amountEstimated),
      amountActual:    exp.amountActual != null ? String(exp.amountActual) : '',
      isPaid:          exp.isPaid,
      isOwnerFee:      exp.isOwnerFee,
      ownerPresent:    exp.ownerPresent,
      notes:           exp.notes ?? '',
    });
    setShowExpenseModal(true);
  };

  const submitExpense = () => {
    if (!expenseForm.description.trim()) { toast.error('Inserisci una descrizione'); return; }
    const est = Number(expenseForm.amountEstimated);
    if (!expenseForm.amountEstimated || isNaN(est) || est < 0) { toast.error('Importo stimato non valido'); return; }
    saveExpenseMutation.mutate({
      category:        expenseForm.category,
      description:     expenseForm.description.trim(),
      amountEstimated: est,
      amountActual:    expenseForm.amountActual ? Number(expenseForm.amountActual) : null,
      isPaid:          expenseForm.isPaid,
      isOwnerFee:      expenseForm.isOwnerFee,
      ownerPresent:    expenseForm.ownerPresent,
      notes:           expenseForm.notes.trim() || null,
    });
  };

  // ── Payment submit helpers ───────────────────────────────────────────────

  // ── Tax helpers ──────────────────────────────────────────────────────────

  const openTaxModal = () => {
    setTaxForm({
      taxMode:        budget?.taxMode ?? event.taxMode ?? '',
      taxRate:        String(budget?.taxRate ?? event.taxRate ?? ''),
      taxCoefficient: String(budget?.taxCoefficient ?? event.taxCoefficient ?? ''),
    });
    setShowTaxModal(true);
  };

  const submitTax = () => {
    const mode = taxForm.taxMode || null;
    patchTaxMutation.mutate({
      taxMode:        mode,
      taxRate:        mode && taxForm.taxRate        ? Number(taxForm.taxRate)        : null,
      taxCoefficient: mode === 'FORFETTARIO' && taxForm.taxCoefficient ? Number(taxForm.taxCoefficient) : null,
    });
  };

  // ── Derived values ───────────────────────────────────────────────────────

  const currentStatusIdx = STATUS_ORDER.indexOf(event.status);
  const nextStatus  = currentStatusIdx < STATUS_ORDER.length - 1 ? STATUS_ORDER[currentStatusIdx + 1] : null;
  const progressPct = ((currentStatusIdx + 1) / STATUS_ORDER.length) * 100;

  const paymentsList = (payments as any[]).length > 0
    ? (payments as any[])
    : (event.depositPaidAt
      ? [{ id: 'legacy-deposit', amount: Number(event.depositAmount ?? 0), paidAt: event.depositPaidAt!, method: event.depositPaidMethod ?? 'CONTANTI', notes: 'Acconto iniziale' } as any]
      : []).concat((event.finalPaidAt
        ? [{ id: 'legacy-final', amount: Number(event.finalAmount ?? 0), paidAt: event.finalPaidAt!, method: event.finalPaidMethod ?? 'CONTANTI', notes: 'Saldo finale' } as any]
        : []));

  const totalPaid = paymentsList.reduce((s: number, p: any) => s + Number(p.amount), 0);
  const remaining = Number(event.totalAmount) - totalPaid;

  const servicesGross = (event.services ?? []).reduce((s, es) => s + Number(es.unitPrice), 0);
  const servicesNet   = (event.services ?? []).reduce((s, es) => s + effectivePrice(es), 0);
  const totalDiscount = servicesGross - servicesNet;
  const hasDiscounts  = totalDiscount > 0.001;
  const revenueBase     = Number(event.totalAmount) > 0 ? Number(event.totalAmount) : servicesNet;

  const expenses          = event.expenses ?? [];
  const expensesActive    = expenses.filter(e => !(e.isOwnerFee && !e.ownerPresent));
  const expensesExcluded  = expenses.filter(e => e.isOwnerFee && !e.ownerPresent);
  const expensesTotal     = expensesActive.reduce((s, e) => s + Number(e.amountActual ?? e.amountEstimated), 0);
  const expensesExcludedTotal = expensesExcluded.reduce((s, e) => s + Number(e.amountEstimated), 0);

  const collaborators = (event.teams ?? []).flatMap(t => t.slots);
  const collaboratorsFeeLocal = collaborators.reduce(
    (s, ec) => s + Number(ec.agreedFee ?? ec.proposedFee ?? 0), 0
  );
  const totalSpese = collaboratorsFeeLocal + expensesTotal;

  // ── Derived: summary cards ───────────────────────────────────────────────

  const postProdTasks = event.postProduction?.tasks ?? [];
  const postProdCompleted = postProdTasks.filter(t => t.status === 'COMPLETED').length;
  const postProdCost = event.postProduction?.totalCost ?? 0;

  const checklistItems = event.checklist ?? [];
  const checklistDone  = checklistItems.filter(i => i.completed).length;

  const attachmentsCount = event.attachments?.length ?? 0;

  const scalettaFields = event.scaletta ? [
    !!(event.scaletta.subject1Name || event.scaletta.subject1Time),
    !!(event.scaletta.subject2Name || event.scaletta.subject2Time),
    !!(event.scaletta.churchName   || event.scaletta.ceremonyTime),
    !!(event.scaletta.preDinnerLocation || event.scaletta.preDinnerTime),
    !!(event.scaletta.restaurantName    || event.scaletta.restaurantTime),
    !!event.scaletta.notes,
  ] : Array(6).fill(false);
  const scalettaFilled = scalettaFields.filter(Boolean).length;
  const SCALETTA_TOTAL = 6;

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* Over-budget alert */}
      {budget?.overBudget && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', background: 'var(--color-error-highlight)', border: '1px solid var(--color-error)', borderRadius: 'var(--radius-card)', padding: '0.75rem 1rem' }}>
          <AlertTriangle size={15} style={{ color: 'var(--color-error)', flexShrink: 0, marginTop: '0.1rem' }} />
          <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text)', lineHeight: 1.5 }}>
            <strong>Attenzione — Budget superato:</strong> i costi stimati ({formatCurrency(budget.totalExpenses)}) superano
            i ricavi netti di <strong style={{ color: 'var(--color-error)' }}>{formatCurrency(Math.abs(budget.grossMargin))}</strong>.
          </p>
        </div>
      )}

      {/* ── Sommari sezioni ───────────────────────────────────────────── */}
      <div className="g3" style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.625rem' }}>
        <SummaryCard
          icon={<ClipboardList size={14} />} title="Servizi"
          onClick={onSwitchTab ? () => onSwitchTab('services') : undefined}
          lines={[
            `${event.services?.length ?? 0} servizi`,
            `Totale ${formatCurrency(servicesNet)}`,
          ]}
        />
        <SummaryCard
          icon={<Calendar size={14} />} title="Scaletta"
          onClick={onSwitchTab ? () => onSwitchTab('scaletta') : undefined}
          lines={[
            event.scaletta ? `${scalettaFilled}/${SCALETTA_TOTAL} sezioni compilate` : 'Non compilata',
            event.scaletta?.shareToken ? 'Link pubblico attivo' : 'Nessun link',
          ]}
        />
        <SummaryCard
          icon={<Users size={14} />} title="Collaboratori"
          onClick={onSwitchTab ? () => onSwitchTab('collaborators') : undefined}
          lines={[
            `${collaborators.length} collaboratori`,
            `Compensi ${formatCurrency(collaboratorsFeeLocal)}`,
          ]}
        />
        <SummaryCard
          icon={<Clapperboard size={14} />} title="Post-Produzione"
          onClick={onSwitchTab ? () => onSwitchTab('post-production') : undefined}
          lines={[
            postProdTasks.length > 0
              ? `${postProdCompleted}/${postProdTasks.length} task completati`
              : 'Nessun task',
            postProdCost > 0 ? `Costo ${formatCurrency(postProdCost)}` : 'Costo —',
          ]}
        />
        <SummaryCard
          icon={<Banknote size={14} />} title="Economica"
          onClick={onSwitchTab ? () => onSwitchTab('economica') : undefined}
          lines={[
            budget
              ? `Ricavi ${formatCurrency(revenueBase)}`
              : 'Nessun dato',
            budget
              ? `Guadagno ${formatCurrency(budget.taxMode ? budget.netProfit : budget.grossMargin)}`
              : '',
          ]}
          highlight={budget ? (budget.overBudget ? 'error' : budget.grossMargin >= 0 ? 'success' : 'warning') : undefined}
        />
        <SummaryCard
          icon={<Folder size={14} />} title="File & Note"
          onClick={onSwitchTab ? () => onSwitchTab('files') : undefined}
          lines={[
            `${attachmentsCount} allegati`,
            checklistItems.length > 0
              ? `Checklist ${checklistDone}/${checklistItems.length}`
              : 'Checklist vuota',
          ]}
        />
      </div>

      {/* Workflow */}
      <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '1.25rem', overflow: 'hidden' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem', gap: '0.5rem' }}>
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600, flexShrink: 0 }}>Workflow</h3>
          {nextStatus && (
            <Button size="sm" onClick={() => { if (statusMutatingRef.current) return; statusMutatingRef.current = true; statusMutation.mutate(nextStatus); }} loading={statusMutation.isPending} icon={<ChevronRight size={14} />} iconPosition="right">
              Avanza a {nextStatus.replace('_', ' ')}
            </Button>
          )}
        </div>
        <ProgressBar value={progressPct} showLabel />
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.5rem', minWidth: 0 }}>
          {STATUS_ORDER.map((s, i) => (
            <div key={s} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.25rem', flex: 1, minWidth: 0, overflow: 'hidden' }}>
              <div style={{ width: 20, height: 20, borderRadius: '50%', background: i <= currentStatusIdx ? 'var(--color-primary)' : 'rgba(255,255,255,0.06)', display: 'flex', alignItems: 'center', justifyContent: 'center', border: i === currentStatusIdx ? '2px solid var(--color-primary)' : 'none', flexShrink: 0 }}>
                {i < currentStatusIdx && <Check size={10} color="white" />}
              </div>
              <span style={{ fontSize: '11px', lineHeight: 1.2, overflowWrap: 'break-word', wordBreak: 'break-word', maxWidth: '100%', textAlign: 'center',
                color: i === currentStatusIdx ? 'var(--color-primary)' : i < currentStatusIdx ? '#A1A1AA' : '#52525B',
                fontWeight: i === currentStatusIdx ? 600 : 500,
              }}>
                {s === 'CONTRATTO' ? 'Contratto\n& Acconto' : s.replace('_', '\n')}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Cliente + Pagamenti */}
      <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
        <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '1.25rem' }}>
          <h3 style={{ fontWeight: 600, marginBottom: '0.75rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em', fontSize: 'var(--text-xs)' }}>Cliente</h3>
          <p style={{ fontWeight: 600, marginBottom: '0.25rem' }}>{clientFullName(event.client)}</p>
          {event.client.email && <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{event.client.email}</p>}
          {event.client.phone && <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{event.client.phone}</p>}
          {event.client.city  && <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', marginTop: '0.25rem' }}>{event.client.city}</p>}
        </div>

        <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '1.25rem' }}>
          <h3 style={{ fontSize: 'var(--text-xs)', fontWeight: 600, marginBottom: '0.75rem', color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Pagamenti</h3>
          {/* totalAmount row — dual display */}
          {editingTotalAmount ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', padding: '0.375rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <input
                type="number" min="0" step="0.01"
                value={totalAmountInput}
                onChange={e => setTotalAmountInput(e.target.value)}
                autoFocus
                style={{ flex: 1, padding: '0.3rem 0.6rem', fontSize: 'var(--text-sm)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', background: 'var(--glass-bg-card)', color: 'var(--color-text)' }}
              />
              <button onClick={() => { const v = Number(totalAmountInput); if (!isNaN(v) && v >= 0) patchTotalAmountMutation.mutate(v); }}
                disabled={patchTotalAmountMutation.isPending}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-success)', display: 'flex' }}><Check size={15} /></button>
              <button onClick={() => setEditingTotalAmount(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', display: 'flex' }}><X size={15} /></button>
            </div>
          ) : (
            <div style={{ padding: '0.375rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '0.5rem' }}>
                <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>Totale concordato</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {(() => {
                    const manualTotal = Number(event.totalAmount);
                    const isOverride = manualTotal > 0 && Math.abs(manualTotal - servicesNet) > 0.01;
                    return (
                      <>
                        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{formatCurrency(manualTotal > 0 ? manualTotal : servicesNet)}</span>
                        {isOverride && (
                          <span style={{ fontSize: '11px', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', display: 'inline-flex', alignItems: 'center', gap: '4px', padding: '2px 10px', borderRadius: '9999px', border: '1px solid #3E91FF', background: 'rgba(62,145,255,0.08)', color: '#60A5FA' }}>override</span>
                        )}
                        <button onClick={() => { setTotalAmountInput(String(event.totalAmount ?? '')); setEditingTotalAmount(true); }}
                          title="Modifica totale concordato"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', display: 'flex', padding: '0.1rem' }}><Pencil size={12} /></button>
                      </>
                    );
                  })()}
                </div>
              </div>
              {servicesNet > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.2rem' }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>Da servizi</span>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{formatCurrency(servicesNet)}</span>
                </div>
              )}
            </div>
          )}
          {paymentsList.length > 0 && (
            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.5rem' }}>
                <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Acconti ricevuti</span>
                <Button size="sm" variant="secondary" onClick={() => { setEditingPaymentId(null); setPaymentForm({ amount: String(remaining > 0 ? remaining : ''), paidAt: new Date().toISOString().split('T')[0], method: 'BONIFICO', notes: '' }); setShowPaymentModal(true); }}>
                  + Acconto
                </Button>
              </div>
              {/* Desktop table */}
              <div className="payment-table-desktop">
                <table style={{ width: '100%', fontSize: 'var(--text-sm)', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid rgba(255,255,255,0.1)', color: 'var(--color-text-faint)', fontSize: 'var(--text-xs)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                      <th style={{ padding: '0.4rem 0', textAlign: 'left', fontWeight: 500 }}>Data</th>
                      <th style={{ padding: '0.4rem 0', textAlign: 'left', fontWeight: 500 }}>Importo</th>
                      <th style={{ padding: '0.4rem 0', textAlign: 'left', fontWeight: 500 }}>Metodo</th>
                      <th style={{ padding: '0.4rem 0', textAlign: 'left', fontWeight: 500 }}>Note</th>
                      <th style={{ padding: '0.4rem 0', textAlign: 'right', fontWeight: 500 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentsList.map((p: any) => (
                      <tr key={p.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
                        <td style={{ padding: '0.5rem 0' }}>{formatDate(p.paidAt)}</td>
                        <td style={{ padding: '0.5rem 0', fontWeight: 600 }}>{formatCurrency(Number(p.amount))}</td>
                        <td style={{ padding: '0.5rem 0' }}>{formatPaymentMethod(p.method)}</td>
                        <td style={{ padding: '0.5rem 0', color: 'var(--color-text-faint)' }}>{p.notes || '—'}</td>
                        <td style={{ padding: '0.5rem 0', textAlign: 'right', whiteSpace: 'nowrap' }}>
                          {!String(p.id).startsWith('legacy-') && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => { setEditingPaymentId(p.id); setPaymentForm({ amount: String(p.amount), paidAt: new Date(p.paidAt).toISOString().split('T')[0], method: p.method, notes: p.notes ?? '' }); setShowPaymentModal(true); }}><Pencil size={12} /></Button>
                              <Button size="sm" variant="ghost" onClick={() => { if (confirm('Rimuovere questo acconto?')) deletePaymentMutation.mutate(p.id); }} loading={deletePaymentMutation.isPending}><X size={12} /></Button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                      <td style={{ padding: '0.5rem 0', fontWeight: 600 }}>Totale versato</td>
                      <td style={{ padding: '0.5rem 0', fontWeight: 600 }}>{formatCurrency(totalPaid)}</td>
                      <td colSpan={3}></td>
                    </tr>
                  </tfoot>
                </table>
              </div>
              {/* Mobile cards */}
              <div className="payment-cards-mobile" style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem' }}>
                {paymentsList.map((p: any) => (
                  <div key={p.id} style={{ display: 'flex', alignItems: 'center', padding: '0.5rem', background: 'rgba(255,255,255,0.03)', borderRadius: '8px' }}>
                    <div style={{ width: '3px', height: '28px', background: 'var(--color-primary)', borderRadius: '2px', marginRight: '0.6rem', flexShrink: 0 }}></div>
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: 'var(--text-sm)', fontWeight: 600 }}>{formatCurrency(Number(p.amount))}</div>
                      <div style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>{formatDate(p.paidAt)} · {formatPaymentMethod(p.method)}{p.notes ? ` · ${p.notes}` : ''}</div>
                    </div>
                    <div style={{ fontSize: 'var(--text-xs)', whiteSpace: 'nowrap', opacity: 0.5 }}>
                      {!String(p.id).startsWith('legacy-') && (
                        <>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingPaymentId(p.id); setPaymentForm({ amount: String(p.amount), paidAt: new Date(p.paidAt).toISOString().split('T')[0], method: p.method, notes: p.notes ?? '' }); setShowPaymentModal(true); }}><Pencil size={12} /></Button>
                          <Button size="sm" variant="ghost" onClick={() => { if (confirm('Rimuovere questo acconto?')) deletePaymentMutation.mutate(p.id); }} loading={deletePaymentMutation.isPending}><X size={12} /></Button>
                        </>
                      )}
                    </div>
                  </div>
                ))}
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.4rem 0.5rem', borderTop: '1px solid rgba(255,255,255,0.08)', fontSize: 'var(--text-sm)', fontWeight: 600 }}>
                  <span>Versato</span>
                  <span>{formatCurrency(totalPaid)}</span>
                </div>
              </div>
              {/* Progress bar */}
              {totalPaid > 0 && Number(event.totalAmount) > 0 && (
                <div style={{ marginTop: '0.5rem' }}>
                  <div style={{ height: '4px', background: 'rgba(255,255,255,0.1)', borderRadius: '2px' }}>
                    <div style={{ width: `${Math.min(100, (totalPaid / Number(event.totalAmount)) * 100)}%`, height: '100%', background: 'var(--color-primary)', borderRadius: '2px' }}></div>
                  </div>
                  <span style={{ fontSize: '10px', color: 'var(--color-text-faint)', display: 'block', textAlign: 'right', marginTop: '0.15rem' }}>
                    {((totalPaid / Number(event.totalAmount)) * 100).toFixed(0)}% incassato
                  </span>
                </div>
              )}
            </div>
          )}
          {remaining > 0 && paymentsList.length > 0 && (
            <div style={{ marginTop: '0.5rem', paddingTop: '0.5rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              <PayRow label="Residuo" value={formatCurrency(remaining)} valueColor="var(--color-warning)" />
            </div>
          )}
          {paymentsList.length === 0 && remaining > 0 && (
            <div style={{ marginTop: '0.5rem' }}>
              <Button size="sm" variant="secondary" onClick={() => { setEditingPaymentId(null); setPaymentForm({ amount: String(remaining), paidAt: new Date().toISOString().split('T')[0], method: 'BONIFICO', notes: '' }); setShowPaymentModal(true); }}>
                + Registra primo acconto
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* ── Ricavi ────────────────────────────────────────────────────────── */}
      <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>Ricavi</h3>
          {onSwitchTab && (
            <Button size="sm" variant="secondary" onClick={() => { toast.info('Usa il tab Servizi per aggiungere o modificare i servizi'); onSwitchTab('servizi'); }} icon={<Plus size={14} />}>Aggiungi</Button>
          )}
        </div>
        {event.services && event.services.length > 0 ? (
          <div>
            {event.services.map(es => {
              const net = effectivePrice(es);
              const hasDiscount = es.discountType != null && es.discount != null;
              const isEditing = editingDiscountId === es.id;
              return (
                <div key={es.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.625rem 0', gap: '0.75rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flex: 1, minWidth: 0 }}>
                      <span style={{ fontSize: 'var(--text-sm)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{es.label}</span>
                      {hasDiscount && (
                        <span style={{ fontSize: '10px', fontWeight: 600, color: 'var(--color-primary)', background: 'oklch(from var(--color-primary) l c h / 0.12)', padding: '0.0625rem 0.375rem', borderRadius: 'var(--radius-interactive)', whiteSpace: 'nowrap', flexShrink: 0 }}>
                          {es.discountType === 'PERCENTAGE' ? `−${es.discount}%` : `−${formatCurrency(es.discount ?? 0)}`}
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexShrink: 0 }}>
                      <div style={{ textAlign: 'right' }}>
                        {hasDiscount && <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', textDecoration: 'line-through', display: 'block' }}>{formatCurrency(es.unitPrice)}</span>}
                        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-gold)' }}>{formatCurrency(net)}</span>
                      </div>
                      <button title={hasDiscount ? 'Modifica sconto' : 'Aggiungi sconto'} onClick={() => isEditing ? setEditingDiscountId(null) : openDiscount(es)}
                        style={{ background: isEditing ? 'var(--color-primary)' : 'none', border: `1px solid ${isEditing ? 'var(--color-primary)' : 'rgba(255,255,255,0.10)'}`, borderRadius: 'var(--radius-interactive)', cursor: 'pointer', color: isEditing ? '#fff' : 'var(--color-text-faint)', padding: '0.25rem', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Percent size={12} />
                      </button>
                      {hasDiscount && !isEditing && (
                        <button title="Rimuovi sconto" onClick={() => removeDiscount(es.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.125rem', display: 'flex' }}><X size={12} /></button>
                      )}
                      <button onClick={() => removeServiceMutation.mutate(es.id)} aria-label="Rimuovi servizio" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-faint)', padding: '0.125rem', display: 'flex' }}><Trash2 size={14} /></button>
                    </div>
                  </div>
                  {isEditing && (
                    <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '0.75rem', marginBottom: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                      <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
                        <Select label="Tipo sconto" value={discountForm.discountType} onChange={e => setDiscountForm(f => ({ ...f, discountType: e.target.value }))} options={DISCOUNT_TYPES} />
                        <Input label={discountForm.discountType === 'PERCENTAGE' ? 'Valore (%)' : 'Importo (€)'} type="number" min="0" step={discountForm.discountType === 'PERCENTAGE' ? '1' : '0.01'} max={discountForm.discountType === 'PERCENTAGE' ? '100' : undefined} value={discountForm.discountValue} onChange={e => setDiscountForm(f => ({ ...f, discountValue: e.target.value }))} />
                      </div>
                      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
                        <Button size="sm" variant="ghost" onClick={() => setEditingDiscountId(null)}>Annulla</Button>
                        <Button size="sm" onClick={() => saveDiscount(es.id)} loading={patchDiscountMutation.isPending}>Salva sconto</Button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
            <div style={{ paddingTop: '0.75rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
              {hasDiscounts && (
                <>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>Lordo</span>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', textDecoration: 'line-through' }}>{formatCurrency(servicesGross)}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)' }}><Tag size={10} style={{ marginRight: '0.25rem', verticalAlign: 'middle' }} />Sconti applicati</span>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)', fontWeight: 600 }}>−{formatCurrency(totalDiscount)}</span>
                  </div>
                </>
              )}
              <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: hasDiscounts ? '0.25rem' : 0, paddingTop: hasDiscounts ? '0.25rem' : 0, borderTop: hasDiscounts ? '1px solid rgba(255,255,255,0.08)' : 'none' }}>
                <span style={{ fontSize: 'var(--text-md)', fontWeight: 700 }}>{hasDiscounts ? 'Netto: ' : 'Totale: '}{formatCurrency(servicesNet)}</span>
              </div>
            </div>
          </div>
        ) : (
          <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: '1rem' }}>Nessun servizio aggiunto</p>
        )}
      </div>

      {/* ── Spese ─────────────────────────────────────────────────────────── */}
      <div style={{ background: 'var(--glass-bg-card)', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-card)', padding: '1.25rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: (collaborators.length > 0 || expenses.length > 0) ? '1rem' : '0' }}>
          <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>Spese</h3>
          <Button size="sm" variant="secondary" onClick={openAddExpense} icon={<Plus size={14} />}>Aggiungi</Button>
        </div>

        {/* ── Compensi collaboratori (read-only) ── */}
        {collaborators.length > 0 && (
          <div style={{ marginBottom: expenses.length > 0 ? '0.75rem' : 0 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.375rem' }}>
              <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                Compensi collaboratori
              </span>
              {onSwitchTab && (
                <button
                  onClick={() => onSwitchTab('collaborators')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 'var(--text-xs)', color: 'var(--color-primary)', padding: 0, textDecoration: 'underline' }}
                >
                  modifica →
                </button>
              )}
            </div>
            {collaborators.map(ec => {
              const effectiveFee = ec.agreedFee != null ? ec.agreedFee : ec.proposedFee;
              const hasOverride = ec.agreedFee != null;
              return (
                <div key={ec.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.375rem 0', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
                  <div>
                    <span style={{ fontSize: 'var(--text-sm)' }}>{ec.collaborator?.name ?? ec.role}</span>
                    <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginLeft: '0.5rem' }}>{ec.role}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
                    {effectiveFee != null ? (
                      <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-primary)' }}>
                        {formatCurrency(effectiveFee)}
                      </span>
                    ) : (
                      <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>—</span>
                    )}
                    {hasOverride && (
                      <span style={{ fontSize: '10px', fontWeight: 700, color: 'var(--color-gold)', background: 'var(--color-gold-highlight)', padding: '0.0625rem 0.3rem', borderRadius: '0.25rem' }}>
                        override
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
            <div style={{ display: 'flex', justifyContent: 'space-between', paddingTop: '0.375rem' }}>
              <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>Totale compensi</span>
              <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-primary)' }}>{formatCurrency(collaboratorsFeeLocal)}</span>
            </div>
          </div>
        )}

        {/* ── Spese manuali ── */}
        {expenses.length === 0 && collaborators.length === 0 ? (
          <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--text-sm)', textAlign: 'center', padding: '1rem' }}>Nessuna spesa registrata</p>
        ) : expenses.length === 0 ? null : (
          <div style={{ borderTop: collaborators.length > 0 ? '1px solid rgba(255,255,255,0.08)' : 'none', paddingTop: collaborators.length > 0 ? '0.75rem' : 0 }}>
            {collaborators.length > 0 && (
              <span style={{ fontSize: 'var(--text-xs)', fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', display: 'block', marginBottom: '0.375rem' }}>
                Spese evento
              </span>
            )}
            {expensesActive.map(exp => (
              <ExpenseRow key={exp.id} expense={exp} onEdit={() => openEditExpense(exp)} onDelete={() => deleteExpenseMutation.mutate(exp.id)} onTogglePaid={() => togglePaidMutation.mutate({ expenseId: exp.id, isPaid: !exp.isPaid })} />
            ))}
            {expensesExcluded.length > 0 && (
              <>
                <div style={{ padding: '0.625rem 0 0.375rem', marginTop: expensesActive.length > 0 ? '0.25rem' : 0 }}>
                  <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>Titolare non presente — non contano come costo</span>
                </div>
                {expensesExcluded.map(exp => (
                  <ExpenseRow key={exp.id} expense={exp} excluded onEdit={() => openEditExpense(exp)} onDelete={() => deleteExpenseMutation.mutate(exp.id)} onTogglePaid={() => togglePaidMutation.mutate({ expenseId: exp.id, isPaid: !exp.isPaid })} />
                ))}
              </>
            )}
          </div>
        )}

        {/* ── Totale complessivo ── */}
        {(collaborators.length > 0 || expenses.length > 0) && (
          <div style={{ paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.08)', marginTop: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            {expensesExcluded.length > 0 && (
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic' }}>Escluse (titolare assente)</span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', textDecoration: 'line-through' }}>{formatCurrency(expensesExcludedTotal)}</span>
              </div>
            )}
            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-muted)' }}>Totale spese</span>
              <span style={{ fontSize: 'var(--text-sm)', fontWeight: 700 }}>{formatCurrency(totalSpese)}</span>
            </div>
          </div>
        )}
      </div>

      {/* ── Margini ───────────────────────────────────────────────────────── */}
      {budget && (
        <div style={{ background: 'var(--glass-bg-card)', border: `1px solid ${budget.overBudget ? 'var(--color-error)' : 'rgba(255,255,255,0.10)'}`, borderRadius: 'var(--radius-card)', padding: '1.25rem' }}>
          {/* Header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
            <h3 style={{ fontSize: 'var(--text-md)', fontWeight: 600 }}>Margini</h3>
            <button onClick={openTaxModal} style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', background: 'none', border: '1px solid rgba(255,255,255,0.10)', borderRadius: 'var(--radius-interactive)', padding: '0.25rem 0.625rem', cursor: 'pointer', fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)' }}>
              <Settings2 size={12} /> Imposte
            </button>
          </div>

          {/* Revenue row */}
          <MarginRow label={hasDiscounts ? 'Ricavi netti (dopo sconti)' : 'Ricavi'} value={revenueBase} valueColor="var(--color-gold)" />

          {/* Cost rows */}
          {budget.productionCosts > 0   && <MarginRow label="− Costi di produzione" value={-budget.productionCosts} />}
          {budget.collaboratorsFee > 0 && <MarginRow label="− Compensi collaboratori" value={-budget.collaboratorsFee} />}
          {budget.otherExpenses > 0     && <MarginRow label="− Spese evento" value={-budget.otherExpenses} />}
          {budget.ownerFeeExcluded > 0  && (
            <MarginRow label="Escluse (titolare assente)" value={0}
              sub={`${formatCurrency(budget.ownerFeeExcluded)} non conteggiati nel margine`} faint />
          )}

          {/* Margine prima delle tasse */}
          <div style={{ marginTop: '0.625rem', paddingTop: '0.625rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <MarginRow
              label="Margine prima delle tasse"
              value={budget.grossMargin}
              bold
              valueColor={budget.grossMargin >= 0 ? 'var(--color-success)' : 'var(--color-error)'}
            />
            <div style={{ marginTop: '0.375rem' }}>
              <ProgressBar
                value={pct(Math.max(0, budget.grossMargin), revenueBase)}
                color={budget.grossMargin >= 0 ? 'var(--color-success)' : 'var(--color-error)'}
                height={4}
              />
            </div>
          </div>

          {/* Tax block (if regime configured) */}
          {budget.taxMode && (
            <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: '1px solid rgba(255,255,255,0.08)' }}>
              {/* Regime badge + params */}
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                <span style={{ fontSize: '10px', fontWeight: 700, color: '#FFFFFF', background: '#3E91FF', padding: '0.125rem 0.5rem', borderRadius: 'var(--radius-interactive)' }}>
                  {budget.taxMode === 'FORFETTARIO' ? 'Forfettario' : 'Ordinario'}
                </span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
                  {budget.taxMode === 'FORFETTARIO' && budget.taxCoefficient != null
                    ? `coeff. ${budget.taxCoefficient}% · aliq. ${budget.taxRate}%`
                    : `aliq. ${budget.taxRate}%`}
                </span>
              </div>
              {budget.taxMode === 'FORFETTARIO' && (
                <MarginRow label="Base imponibile" value={budget.taxableBase}
                  sub={`${budget.taxCoefficient}% del margine lordo`} />
              )}
              <MarginRow label="− Imposte stimate" value={-budget.taxAmount} valueColor="var(--color-text-muted)" />
            </div>
          )}

          {/* ── Il tuo guadagno — prominent box ── */}
          {(() => {
            const finalValue = budget.taxMode ? budget.netProfit : budget.grossMargin;
            const isPositive = finalValue >= 0;
            const profitPct  = revenueBase > 0 ? ((finalValue / revenueBase) * 100) : 0;
            const boxColor   = isPositive ? 'var(--color-success)' : 'var(--color-error)';
            const noTax      = !budget.taxMode;

            return (
              <div style={{
                marginTop: '1.25rem',
                border: `2px solid ${noTax ? 'var(--color-warning)' : boxColor}`,
                borderRadius: 'var(--radius-card)',
                padding: '1rem 1.25rem',
                background: noTax
                  ? 'oklch(from var(--color-warning) l c h / 0.06)'
                  : `oklch(from ${boxColor} l c h / 0.06)`,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '1rem', flexWrap: 'wrap' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <Wallet size={16} style={{ color: noTax ? 'var(--color-warning)' : boxColor, flexShrink: 0 }} />
                    <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: 'var(--color-text-muted)' }}>
                      Il tuo guadagno
                    </span>
                    {noTax && (
                      <span style={{ fontSize: '10px', fontWeight: 700, color: '#FFFFFF', background: 'var(--color-warning)', padding: '0.125rem 0.5rem', borderRadius: '0.25rem', whiteSpace: 'nowrap' }}>
                        Senza calcolo tasse
                      </span>
                    )}
                  </div>
                  <span style={{ fontSize: 'var(--text-xl)', fontWeight: 800, color: boxColor, fontVariantNumeric: 'tabular-nums' }}>
                    {isPositive ? '' : '−'}{formatCurrency(Math.abs(finalValue))}
                  </span>
                </div>

                <div style={{ marginTop: '0.75rem' }}>
                  <ProgressBar
                    value={pct(Math.max(0, finalValue), revenueBase)}
                    color={boxColor}
                    height={8}
                  />
                  {revenueBase > 0 && (
                    <span style={{ fontSize: '12px', fontWeight: 500, color: 'var(--color-text-muted)', display: 'block', textAlign: 'right', marginTop: '0.25rem' }}>
                      {profitPct.toFixed(1)}% del ricavo netto
                    </span>
                  )}
                </div>

                {noTax && (
                  <button
                    onClick={openTaxModal}
                    style={{ marginTop: '0.75rem', background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontSize: 'var(--text-xs)', color: 'var(--color-primary)', textDecoration: 'underline', display: 'block' }}
                  >
                    Configura regime fiscale →
                  </button>
                )}
              </div>
            );
          })()}
        </div>
      )}

      {/* ── Modals ────────────────────────────────────────────────────────── */}

      <Modal open={showPaymentModal} onClose={() => { setShowPaymentModal(false); setEditingPaymentId(null); }}
        title={editingPaymentId ? 'Modifica acconto' : 'Registra acconto'}
        footer={<><Button variant="ghost" onClick={() => { setShowPaymentModal(false); setEditingPaymentId(null); }}>Annulla</Button>
        <Button onClick={() => {
          const amount = Number(paymentForm.amount);
          if (!paymentForm.amount || isNaN(amount) || amount <= 0) { toast.error('Inserisci un importo valido'); return; }
          if (!paymentForm.paidAt) { toast.error('Inserisci la data di pagamento'); return; }
          if (new Date(paymentForm.paidAt) > new Date()) { toast.error('La data di pagamento non può essere futura'); return; }
          const existingAmt = editingPaymentId ? Number(paymentsList.find((p: any) => p.id === editingPaymentId)?.amount ?? 0) : 0;
          if (Number(event.totalAmount) > 0 && amount > remaining + existingAmt) {
            toast.error(`L'importo supera il residuo da incassare`); return;
          }
          if (editingPaymentId) editPaymentMutation.mutate(); else addPaymentMutation.mutate();
        }} loading={addPaymentMutation.isPending || editPaymentMutation.isPending}>
        {editingPaymentId ? 'Salva' : 'Aggiungi'}
      </Button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Input label="Importo (€) *" type="number" min="0" step="0.01" value={paymentForm.amount} onChange={e => setPaymentForm(f => ({ ...f, amount: e.target.value }))} required />
          <Input label="Data pagamento *" type="date" value={paymentForm.paidAt} onChange={e => setPaymentForm(f => ({ ...f, paidAt: e.target.value }))} required />
          <Select label="Metodo *" value={paymentForm.method} onChange={e => setPaymentForm(f => ({ ...f, method: e.target.value }))} options={PAYMENT_METHODS} />
          <Input label="Note (opzionale)" value={paymentForm.notes} onChange={e => setPaymentForm(f => ({ ...f, notes: e.target.value }))} placeholder="es. Seconda rata" />
        </div>
      </Modal>

      {/* Expense modal */}
      <Modal open={showExpenseModal} onClose={() => setShowExpenseModal(false)} title={editingExpense ? 'Modifica spesa' : 'Aggiungi spesa'}
        footer={<><Button variant="ghost" onClick={() => setShowExpenseModal(false)}>Annulla</Button><Button onClick={submitExpense} loading={saveExpenseMutation.isPending}>{editingExpense ? 'Salva' : 'Aggiungi'}</Button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Select label="Categoria *" value={expenseForm.category} onChange={e => setExpenseForm(f => ({ ...f, category: e.target.value as EventExpenseCategory }))} options={EXPENSE_CATEGORIES} />
          <Input label="Descrizione *" value={expenseForm.description} onChange={e => setExpenseForm(f => ({ ...f, description: e.target.value }))} placeholder="es. Carburante A/R, Album GraphiStudio..." />
          <div className="g2" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem' }}>
            <Input label="Importo stimato (€) *" type="number" min="0" step="0.01" value={expenseForm.amountEstimated} onChange={e => setExpenseForm(f => ({ ...f, amountEstimated: e.target.value }))} />
            <Input label="Importo effettivo (€)" type="number" min="0" step="0.01" value={expenseForm.amountActual} onChange={e => setExpenseForm(f => ({ ...f, amountActual: e.target.value }))} placeholder="Se diverso da stimato" />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={expenseForm.isOwnerFee} onChange={e => setExpenseForm(f => ({ ...f, isOwnerFee: e.target.checked, ownerPresent: e.target.checked ? f.ownerPresent : true }))} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--color-primary)' }} />
            <span style={{ fontSize: 'var(--text-sm)' }}>Compenso titolare</span>
          </label>
          {expenseForm.isOwnerFee && (
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.625rem', cursor: 'pointer', userSelect: 'none', background: 'var(--glass-bg-card)', borderRadius: 'var(--radius-card)', padding: '0.625rem 0.75rem', border: '1px solid rgba(255,255,255,0.10)' }}>
              <input type="checkbox" checked={expenseForm.ownerPresent} onChange={e => setExpenseForm(f => ({ ...f, ownerPresent: e.target.checked }))} style={{ width: 16, height: 16, cursor: 'pointer', marginTop: '0.125rem', accentColor: 'var(--color-primary)', flexShrink: 0 }} />
              <div>
                <span style={{ fontSize: 'var(--text-sm)', display: 'block' }}>Titolare presente all'evento</span>
                <span style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)' }}>
                  {expenseForm.ownerPresent ? 'La spesa viene conteggiata nei costi' : 'Non spuntato → esclusa dal margine (titolare non presente = ricavo pieno)'}
                </span>
              </div>
            </label>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: '0.625rem', cursor: 'pointer', userSelect: 'none' }}>
            <input type="checkbox" checked={expenseForm.isPaid} onChange={e => setExpenseForm(f => ({ ...f, isPaid: e.target.checked }))} style={{ width: 16, height: 16, cursor: 'pointer', accentColor: 'var(--color-primary)' }} />
            <span style={{ fontSize: 'var(--text-sm)' }}>Già pagata</span>
          </label>
          <Input label="Note (opzionale)" value={expenseForm.notes} onChange={e => setExpenseForm(f => ({ ...f, notes: e.target.value }))} placeholder="Note aggiuntive..." />
        </div>
      </Modal>

      {/* Tax config modal */}
      <Modal open={showTaxModal} onClose={() => setShowTaxModal(false)} title="Configura imposte"
        footer={<><Button variant="ghost" onClick={() => setShowTaxModal(false)}>Annulla</Button><Button onClick={submitTax} loading={patchTaxMutation.isPending}>Salva</Button></>}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          <Select
            label="Regime fiscale"
            value={taxForm.taxMode}
            onChange={e => setTaxForm(f => ({ ...f, taxMode: e.target.value, taxCoefficient: e.target.value !== 'FORFETTARIO' ? '' : f.taxCoefficient }))}
            options={TAX_MODE_OPTIONS}
          />
          {taxForm.taxMode === 'FORFETTARIO' && (
            <Input
              label="Coefficiente di redditività (%)"
              type="number" min="0" max="100" step="1"
              value={taxForm.taxCoefficient}
              onChange={e => setTaxForm(f => ({ ...f, taxCoefficient: e.target.value }))}
              placeholder="es. 78"
            />
          )}
          {taxForm.taxMode !== '' && (
            <Input
              label={taxForm.taxMode === 'FORFETTARIO' ? 'Aliquota imposta (%)' : 'Aliquota IRPEF (%)'}
              type="number" min="0" max="100" step="0.5"
              value={taxForm.taxRate}
              onChange={e => setTaxForm(f => ({ ...f, taxRate: e.target.value }))}
              placeholder={taxForm.taxMode === 'FORFETTARIO' ? 'es. 15' : 'es. 26'}
            />
          )}
          <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', fontStyle: 'italic', lineHeight: 1.5 }}>
            I valori di default vengono da <strong>Impostazioni → Finanza</strong>.
            Modificarli qui sovrascrive solo questo evento.
          </p>
        </div>
      </Modal>
    </div>
  );
}

function SummaryCard({ icon, title, lines, onClick, highlight }: {
  icon: React.ReactNode; title: string; lines: string[];
  onClick?: () => void;
  highlight?: 'success' | 'error' | 'warning';
}) {
  const borderColor = highlight === 'success' ? 'var(--color-success)' : highlight === 'error' ? 'var(--color-error)' : highlight === 'warning' ? 'var(--color-warning)' : 'rgba(255,255,255,0.10)';
  return (
    <div
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? e => { if (e.key === 'Enter' || e.key === ' ') onClick(); } : undefined}
      style={{
        background: 'var(--glass-bg-card)',
        border: `1px solid ${borderColor}`,
        borderRadius: 'var(--radius-card)',
        padding: '0.75rem 0.875rem',
        cursor: onClick ? 'pointer' : 'default',
        transition: 'border-color var(--transition-interactive), background var(--transition-interactive)',
      }}
      onMouseEnter={onClick ? e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--glass-bg-card)'; } : undefined}
      onMouseLeave={onClick ? e => { (e.currentTarget as HTMLDivElement).style.background = 'var(--glass-bg-card)'; } : undefined}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem', marginBottom: '0.3rem' }}>
        <span style={{ fontSize: '14px', lineHeight: 1 }}>{icon}</span>
        <span style={{ fontSize: 'var(--text-xs)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em', color: 'var(--color-text-muted)' }}>{title}</span>
      </div>
      {lines.filter(Boolean).map((line, i) => (
        <p key={i} style={{ fontSize: 'var(--text-sm)', color: i === 0 ? 'var(--color-text)' : 'var(--color-text-muted)', lineHeight: 1.4, marginTop: i > 0 ? '0.1rem' : 0 }}>{line}</p>
      ))}
      {onClick && (
        <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-primary)', marginTop: '0.375rem' }}>Vai →</p>
      )}
    </div>
  );
}

function BudgetRow({ label, value, valueColor, bold }: { label: string; value: number; valueColor?: string; bold?: boolean }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '0.25rem 0' }}>
      <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)', fontWeight: bold ? 600 : 400 }}>{label}</span>
      <span style={{ fontSize: 'var(--text-sm)', fontWeight: bold ? 700 : 500, color: valueColor ?? 'var(--color-text)' }}>{formatCurrency(value)}</span>
    </div>
  );
}

function PayRow({ label, value, sub, action, valueColor }: { label: string; value: string; sub?: string; action?: React.ReactNode; valueColor?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.375rem 0', gap: '0.5rem' }}>
      <div>
        <span style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{label}</span>
        {sub && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-faint)', marginTop: '0.0625rem' }}>{sub}</p>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
        <span style={{ fontSize: 'var(--text-sm)', fontWeight: 600, color: valueColor ?? 'var(--color-text)' }}>{value}</span>
        {action}
      </div>
    </div>
  );
}
